/**
 * Assets roster → Vehicle Type + Payload labels for Configuration quantity.
 * Uses the lightweight /vehicles/class-labels endpoint (no full Assets list /
 * VehicleObserved Trino batch).
 */

import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from './authenticatedFetch';
import { readSessionUserEmail } from '../auth/cognitoAuth';
import type { AssetClassLabel } from './fleetCostAssumptionsQty';

/**
 * Session copy (memory only), so Configuration remounts don't wait on the
 * server. Keyed by the signed-in user: sign-out does not reload the page.
 */
const CLIENT_TTL_MS = 5 * 60_000;
let cached: { user: string; at: number; labels: Promise<AssetClassLabel[]> } | null = null;

/** Drop the session copy — call after vehicle attributes are saved. */
export function clearFleetCostAssetClassLabelsCache(): void {
  cached = null;
}

export async function fetchFleetCostAssetClassLabels(): Promise<AssetClassLabel[]> {
  const user = readSessionUserEmail();
  if (cached && user && cached.user === user && Date.now() - cached.at < CLIENT_TTL_MS) {
    return cached.labels;
  }
  const labels = loadClassLabels();
  cached = user ? { user, at: Date.now(), labels } : null;
  // A failed load is not kept.
  labels.catch(() => {
    if (cached?.labels === labels) cached = null;
  });
  return labels;
}

async function loadClassLabels(): Promise<AssetClassLabel[]> {
  const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.vehicles.classLabels());
  if (!res.ok) throw new Error(`Asset class labels failed (${res.status})`);
  const json = (await res.json()) as {
    labels?: Array<{ type?: string; payloadTonnes?: string; registration?: string; acquisition?: string; trailer_deck?: string }>;
  };

  return (json.labels ?? []).map((row) => {
    const acquisition = String(row.acquisition ?? '').trim().toLowerCase();
    const deck = String(row.trailer_deck ?? '').trim().toLowerCase();
    return {
      ...(deck === 'single' || deck === 'double' ? { trailer_deck: deck as 'single' | 'double' } : {}),
      type: String(row.type ?? '').trim(),
      payloadTonnes: String(row.payloadTonnes ?? '').trim(),
      ...(acquisition === 'purchase' || acquisition === 'lease' || acquisition === 'rental'
        ? { acquisition: acquisition as 'purchase' | 'lease' | 'rental' }
        : {}),
    };
  });
}
